import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  mergeMap,
  switchMap,
  tap
} from 'rxjs/operators';
import { Logger } from '@app/utils/logger';
import { EVENT } from '@providers/event/event.enum';
import { EventService } from '@providers/event/event.service';
import { Session } from '@providers/session/session.service';
import { AvatarCacheService } from '../services/avatar-cache.service';
import * as DnaReportAvatarActions from './dna-report-avatar.actions';
import { DnaReportAvatarService } from './dna-report-avatar.service';

/**
 * DNA Report Avatar Effects
 * NgRx effects for DNA report avatar functionality.
 */
@Injectable()
export class DnaReportAvatarEffects {
  private readonly actions$ = inject(Actions);
  private readonly avatarService = inject(DnaReportAvatarService);
  private readonly cacheService = inject(AvatarCacheService);
  private readonly eventService = inject(EventService);
  private readonly session = inject(Session);
  private readonly log = Logger.getInstance('DnaReportAvatarEffects');

  /**
   * Effect: Fetch bulk avatar images
   * Fetches multiple avatar images in a single API call.
   */
  fetchBulkAvatarImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DnaReportAvatarActions.fetchBulkAvatarImages),
      tap(({ profileId, imageTypes }) => {
        this.log.log(
          '[DnaReportAvatarEffects] fetchBulkAvatarImages action received:',
          {
            profileId,
            imageTypes
          }
        );
      }),
      switchMap(({ profileId, imageTypes }) =>
        this.avatarService.getBulkAvatarImages(profileId, imageTypes).pipe(
          map((response) => {
            this.log.log(
              '[DnaReportAvatarEffects] Bulk avatar images fetched:',
              response
            );

            // Cache non-default images
            response.data.images.forEach((img) => {
              if (!img.isDefault) {
                this.cacheService.set(profileId, img.imageType, img.imageUrl);
              }
            });

            return DnaReportAvatarActions.fetchBulkAvatarImagesSuccess({
              profileId,
              images: response.data.images
            });
          }),
          catchError((error) => {
            this.log.error(
              '[DnaReportAvatarEffects] Error fetching bulk avatar images:',
              error
            );
            return of(
              DnaReportAvatarActions.fetchBulkAvatarImagesFailure({
                error: error.message ?? 'Failed to fetch avatar images'
              })
            );
          })
        )
      )
    )
  );

  /**
   * Effect: Fetch single avatar image
   * Checks cache first, then fetches from API.
   */
  fetchSingleAvatarImage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DnaReportAvatarActions.fetchSingleAvatarImage),
      mergeMap(({ profileId, imageType }) => {
        // Check cache first
        const cachedUrl = this.cacheService.get(profileId, imageType);

        if (cachedUrl) {
          this.log.log(
            '[DnaReportAvatarEffects] Avatar image found in cache:',
            cachedUrl
          );
          return of({
            fromCache: true,
            profileId,
            imageType,
            imageUrl: cachedUrl
          });
        }

        return of({
          fromCache: false,
          profileId,
          imageType,
          imageUrl: null
        });
      }),
      switchMap(({ fromCache, profileId, imageType, imageUrl }) => {
        if (fromCache && imageUrl) {
          return of(
            DnaReportAvatarActions.fetchSingleAvatarImageSuccess({
              imageType,
              imageUrl,
              isDefault: false
            })
          );
        }

        // Fetch from API
        return this.avatarService.getAvatarImage(profileId, imageType).pipe(
          map((response) => {
            this.log.log(
              '[DnaReportAvatarEffects] Avatar image fetched from API:',
              response
            );

            // Cache non-default images
            if (!response.data.isDefault) {
              this.cacheService.set(
                profileId,
                imageType,
                response.data.imageUrl
              );
            }

            return DnaReportAvatarActions.fetchSingleAvatarImageSuccess({
              imageType,
              imageUrl: response.data.imageUrl,
              isDefault: response.data.isDefault,
              width: response.data.width,
              height: response.data.height
            });
          }),
          catchError((error) => {
            this.log.error(
              '[DnaReportAvatarEffects] Error fetching avatar image:',
              error
            );
            return of(
              DnaReportAvatarActions.fetchSingleAvatarImageFailure({
                imageType,
                error: error.message ?? 'Failed to fetch avatar image'
              })
            );
          })
        );
      })
    )
  );

  /**
   * Effect: Invalidate cache.
   */
  invalidateCache$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(DnaReportAvatarActions.invalidateCache),
        tap(({ profileId, imageType }) => {
          this.log.log('[DnaReportAvatarEffects] Invalidating cache:', {
            profileId,
            imageType
          });
          this.cacheService.invalidate(profileId, imageType);
        })
      ),
    { dispatch: false }
  );

  /**
   * Effect: Handle avatar notification for DNA reports
   * Listens to AVATAR_NOTIFICATION event and triggers refresh.
   */
  onAvatarNotification$ = createEffect(() =>
    this.eventService.listen(EVENT.AVATAR_NOTIFICATION).pipe(
      filter((notification) =>
        ['AVATAR_COMPLETED', 'AVATAR_FAILED'].includes(notification.type)
      ),
      tap((notification) => {
        this.log.log(
          '[DnaReportAvatarEffects] Avatar notification received:',
          notification
        );
      }),
      switchMap((notification) => {
        const profileId =
          notification.profileId ?? this.session.getProfile()?.id;

        if (!profileId) {
          this.log.warn(
            '[DnaReportAvatarEffects] No profile ID available for notification'
          );
          return EMPTY;
        }

        if (notification.type === 'AVATAR_COMPLETED') {
          // Refresh all DNA report images
          return of(
            DnaReportAvatarActions.refreshAvatarImages({
              profileId
            })
          );
        }

        // For failed notifications, just update processing state
        return of(
          DnaReportAvatarActions.setProcessing({
            isProcessing: false
          })
        );
      })
    )
  );

  /**
   * Effect: Handle logout - clear state
   * Clears all avatar caches from localStorage and resets state.
   */
  onLogout$ = createEffect(() =>
    this.eventService.listen(EVENT.LOGOUT).pipe(
      tap(() => {
        this.log.log('[DnaReportAvatarEffects] Logout event detected');
        this.log.log('[DnaReportAvatarEffects] Clearing all avatar caches');
        // Clear all avatar caches from localStorage
        this.cacheService.clear();
      }),
      map(() => DnaReportAvatarActions.clearDnaReportAvatarState())
    )
  );

  /**
   * Effect: Handle profile switch
   * Invalidates old profile cache and clears state (components will refetch when needed).
   */
  onProfileSwitch$ = createEffect(() =>
    this.eventService.listen(EVENT.PROFILE_SWITCHED).pipe(
      tap((event) => {
        this.log.log('[DnaReportAvatarEffects] Profile switch event detected');
        // Invalidate OLD profile cache if provided
        if (event?.oldProfileId) {
          this.log.log(
            '[DnaReportAvatarEffects] Invalidating cache for old profile:',
            event.oldProfileId
          );
          this.cacheService.invalidate(event.oldProfileId);
        }
      }),
      // DON'T fetch here - clear state and let components refetch when needed
      // This prevents race condition where fetch happens with stale profile ID
      map(() => DnaReportAvatarActions.clearDnaReportAvatarState())
    )
  );

  /**
   * Effect: Handle profile update
   * Invalidates default avatar cache when profile is updated (e.g., gender change).
   */
  onProfileUpdate$ = createEffect(() =>
    this.eventService.listen(EVENT.PROFILE_UPDATED).pipe(
      tap(() => {
        this.log.log('[DnaReportAvatarEffects] Profile update event detected');
      }),
      switchMap((event) => {
        const profileId = event?.profileId ?? this.session.getProfile()?.id;
        if (profileId) {
          this.log.log(
            '[DnaReportAvatarEffects] Invalidating default avatar cache for profile:',
            profileId
          );
          // Invalidate only HOMEPAGE (default avatar) cache
          this.cacheService.invalidate(profileId, 'HOMEPAGE');
          return of(
            DnaReportAvatarActions.refreshAvatarImages({
              profileId
            })
          );
        }
        return EMPTY;
      })
    )
  );

  /**
   * Effect: Refresh avatar images after notification
   * Invalidates cache and re-fetches images.
   */
  refreshAvatarImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DnaReportAvatarActions.refreshAvatarImages),
      tap(({ profileId }) => {
        this.log.log(
          '[DnaReportAvatarEffects] Invalidating cache for profile:',
          profileId
        );
        this.cacheService.invalidate(profileId);
      }),
      map(({ profileId, imageTypes }) => {
        const types =
          imageTypes ?? this.avatarService.getDefaultDnaReportImageTypes();
        return DnaReportAvatarActions.fetchBulkAvatarImages({
          profileId,
          imageTypes: types
        });
      })
    )
  );
}
